import { PadBox, WindowTitle } from "component/basics";
import { Structure } from "util/stdtypes";
import { CTAButton } from "component/button";
import { VertBox, HorizBox, Pad, Separator } from "component/basics";
import { callServerApiAsync } from "system/servercall";
import { Heading, UtilityText } from "component/text";
import { getDeploymentConfig } from "util/util";
import { View } from "react-native";
import { useDatastore } from "util/datastore";
import { useEffect, useState } from "react";


export type DeveloperConfig = {
    panels?: React.ComponentType[];
}

export const DeveloperToolsStructure : Structure<DeveloperConfig> = {
    key: 'developer-tools',
    isAdminUi: true,
    name: 'Developer Tools',
    screen: DeveloperToolsScreen,
}

export function DeveloperToolsScreen() {

    return <View>
        <PadBox horiz={20} vert={20}>
            <WindowTitle text="Developer Tools" />
            <Heading text="Developer Tools" type="large" />
            <Pad size={16} />
            <Separator />
            <Pad size={16} />
            <ClientEnvironmentVariablesPanel />
            <Pad size={16} />
            <Separator />
            <Pad size={16} />
            <ServerEnvironmentVariablesPanel />
            <Pad size={16} />
            <Separator />
            <Pad size={16} />
            <ErrorMonitoringPanel />
        </PadBox>
    </View>
}

function ClientEnvironmentVariablesPanel() {
    const deploymentConfig = getDeploymentConfig();
    return <EnvironmentVariablesList
        variables={deploymentConfig}
        title="Client-side configuration"
    />
}

function ServerEnvironmentVariablesPanel() {
    const datastore = useDatastore();
    const [serverEnvironmentVariables, setServerEnvironmentVariables] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        callServerApiAsync({
            datastore,
            component: 'debug',
            funcname: 'inspectEnvironmentVariables'
        }).then((data) => {
            setServerEnvironmentVariables(data || {});
            setIsLoading(false);
        });
    }, []);
    return <EnvironmentVariablesList
        variables={serverEnvironmentVariables}
        title="Server-side configuration"
        isLoading={isLoading}
    />
}

function ErrorMonitoringPanel() {
    const datastore = useDatastore();

    const triggerClientError = () => {
        // This will trigger a client-side error that should be caught by Sentry and our error monitoring
        throw new Error(`Testing client-side error from developer tools - ${new Date().toISOString()}`);
    };

    const triggerServerError = async () => {
        // This will make an API call to trigger a server-side error
        // The error will be handled by the server's error monitoring system
        try {
            await callServerApiAsync({
                datastore,
                component: 'debug',
                funcname: 'triggerTestError'
            });
        } catch (error) {
            // Intentionally ignoring this error. It will be handled by the server.
        }
    };

    return <VertBox>
        <Heading text="Error Monitoring" />
        <Separator />
        <Pad size={16} />
        <UtilityText text="Use these buttons to trigger errors and test your error monitoring system." />
        <Pad size={16} />
        <HorizBox gap={16}>
            <CTAButton
                text="Trigger client-side error"
                onPress={triggerClientError}
                type="secondary"
            />
            <CTAButton
                text="Trigger server-side error"
                onPress={triggerServerError}
                type="secondary"
            />
        </HorizBox>
    </VertBox>
}

function EnvironmentVariablesList({
    variables,
    title,
    isLoading = false
}: {
    variables: Record<string, any>;
    title: string;
    isLoading?: boolean;
}) {
    const [showAll, setShowAll] = useState(false);
    const variableKeys = typeof variables === 'object' ? Object.keys(variables) : [];
    const displayKeys = showAll ? variableKeys : variableKeys.slice(0, 5);
    const hasMore = variableKeys.length > 5;

    return <VertBox>
        <Heading text={title} />
        <Pad size={16} />
        {isLoading ? <UtilityText text="Loading..." /> : null}
        {displayKeys.length > 0 ? <VertBox gap={8}>
            {displayKeys.map((key) => {
                const value = typeof variables[key] === 'object' ? JSON.stringify(variables[key]) : variables[key];
                return <View key={key}>
                    <UtilityText text={key} weight="medium" />
                    <Pad size={4} />
                    <UtilityText text={`${value}`} mono />
                </View>
            })}
        </VertBox> : <UtilityText text="Unable to display environment variables." />}
        {hasMore && (
            <>
                <Pad size={8} />
                <CTAButton
                    text={showAll ? "Show fewer" : "Show all"}
                    onPress={() => setShowAll(!showAll)}
                    type="secondary"
                    size="small"
                />
            </>
        )}
    </VertBox>
}